import React, { useCallback, useEffect } from 'react';
import { useQuizzStore } from '../../stores';
import { useNavigate } from 'react-router';
import { ERoutes } from '../../enums';
import theme, { TMaterialStyles } from '../../theme';
import { useTimer } from '../../hooks/useTimer';
import { getQuizzAvailableState } from '../../queries/quizz';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Icon } from '../Icon';
import { useTranslation } from 'react-i18next';
import { customToast } from '../../utils';
import { useAvailableQuizzState } from '../../hooks';
import { formatDateToFormatWithWeeks } from '../../helpers';

interface IQuizzButton {}

export const QuizzButton: React.FC<IQuizzButton> = () => {
  const { availableAt, availableUntil } = useQuizzStore();
  const isQuizzActive = useAvailableQuizzState();
  const { t } = useTranslation();

  const timer = useTimer(availableAt, undefined, 'DAYS_OR_TIME');

  const navigate = useNavigate();

  const onGoToQuizz = useCallback(() => {
    if (!isQuizzActive) {
      customToast(
        {
          title: t('quizz.quiz_unlocks_soon'),
          subtitle: t('quizz.quizz_not_available', {
            from: formatDateToFormatWithWeeks(availableAt),
            to: formatDateToFormatWithWeeks(availableUntil),
          }),
        },
        'info',
      );
      return;
    }
    navigate(ERoutes.quizzStart);
  }, [availableAt, availableUntil, isQuizzActive, navigate, t]);

  useEffect(() => {
    if (!isQuizzActive && !timer) {
      getQuizzAvailableState();
    }
  }, [isQuizzActive, timer]);

  return (
    <ButtonBase onClick={onGoToQuizz} sx={materialStyles.quizButton}>
      <Box sx={materialStyles.quizzButtonContainer}>
        <Box sx={materialStyles.timerContainer}>
          <Typography sx={materialStyles.buttonText}>{timer}</Typography>
        </Box>
        <Icon sx={materialStyles.quizzButtonIcon} iconName="QuizzIcon" />
        <Box sx={materialStyles.titleContainer}>
          <Typography sx={materialStyles.buttonText}>
            {t('quizz.quizz')}
          </Typography>
        </Box>
      </Box>
    </ButtonBase>
  );
};

const materialStyles: TMaterialStyles = {
  quizButton: {
    position: 'absolute',
    left: '16px',
    top: '40%',
    zIndex: 2,
  },
  quizzButtonContainer: {
    display: 'flex',
    width: '55px',
    height: '55px',
    borderRadius: '14px',
    backgroundColor: theme.palette.primary.main,
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #FFFFFF',
  },
  quizzButtonIcon: {
    width: '25px',
    height: '25px',
  },
  timerContainer: {
    backgroundColor: 'white',
    position: 'absolute',
    top: '-2px',
    borderRadius: '14px',
  },
  titleContainer: {
    backgroundColor: 'white',
    position: 'absolute',
    bottom: '-4px',
    borderRadius: '14px',
  },
  buttonText: {
    color: theme.palette.secondary.main,
    fontSize: '12px',
    lineHeight: '12px',
    whiteSpace: 'nowrap',
    px: '5px',
  },
};
